import { createV1Backend } from '@daily/client';
import type { V1AuthSource, V1DataSource } from '@daily/client';
import { loadFixtureDataSource } from '@daily/ui';
import type { AuthSource, DataSource, FixtureDataSource, FixtureOptions } from '@daily/ui';

/**
 * The one place that chooses the UI's data source (design-handoff/06 §2).
 *
 * - Production builds always use the backend (P8): the social API of this page's origin.
 * - Development builds use the backend too, unless the address asks for fixtures (`?fixtures`, or
 *   any `fx.` switch): then the prototype's fictional group, in memory. `import.meta.env.DEV` is
 *   false in production builds, so the fixture module is never bundled there (checked in the log).
 */
export interface ChosenDataSource {
  readonly data: DataSource;
  readonly auth: AuthSource;
  /** Set only for fixtures, so tests and the development panel can flip the review switches. */
  readonly fixture: FixtureDataSource | null;
}

/** The backend adapter in `@daily/client` keeps its own copy of the port; this fails to compile if the copies drift apart. */
type Same<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
type Assert<T extends true> = T;
export type BackendMatchesUiPort = Assert<Same<V1DataSource, DataSource>> | Assert<Same<V1AuthSource, AuthSource>>;

const ENUMS: { readonly [K in keyof FixtureOptions]?: readonly string[] } = {
  history: ['populated', 'quiet'], monthLoad: ['loaded', 'loading', 'error'], dayCrowd: ['fixture', '0', '1', '6', '24'],
  dayLoad: ['loaded', 'loading', 'failed'], friendGrid: ['as saved', 'one item', 'full'],
};
const FLAGS: readonly (keyof FixtureOptions)[] = ['saveFails', 'uploadFails', 'brokenMedia', 'signedOut', 'noProfile', 'firstRun', 'postedToday'];
const NUMBERS: readonly (keyof FixtureOptions)[] = ['latency', 'postSaveMs', 'profileSaveMs', 'dayShift'];

/** Fixture switches from the address bar, e.g. `?fx.dayCrowd=24&fx.saveFails=1`. Unknown values are ignored. */
export function fixtureOptionsFrom(search: string): Partial<FixtureOptions> {
  const params = new URLSearchParams(search), out: Record<string, unknown> = {};
  for (const [key, allowed] of Object.entries(ENUMS)) { const value = params.get(`fx.${key}`); if (value !== null && allowed?.includes(value)) out[key] = value; }
  for (const key of FLAGS) { const value = params.get(`fx.${key}`); if (value !== null) out[key] = value === '1' || value === 'true'; }
  for (const key of NUMBERS) { const value = Number(params.get(`fx.${key}`)); if (params.has(`fx.${key}`) && Number.isFinite(value) && value >= 0) out[key] = value; }
  return out as Partial<FixtureOptions>;
}

/** Development only: the address asks for the fixture group (`?fixtures`, or any `fx.` switch). */
export function wantsFixtures(search: string): boolean {
  const params = new URLSearchParams(search);
  return params.has('fixtures') || [...params.keys()].some((key) => key.startsWith('fx.'));
}

export async function chooseDataSource(search: string): Promise<ChosenDataSource> {
  if (import.meta.env.DEV && wantsFixtures(search)) {
    const { createFixtureAuth, createFixtureDataSource } = await loadFixtureDataSource();
    const fixture = createFixtureDataSource(fixtureOptionsFrom(search));
    return { data: fixture, auth: createFixtureAuth(fixture), fixture };
  }
  const backend = await createV1Backend();
  return { data: backend.data, auth: backend.auth, fixture: null };
}
